import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/i18n/LanguageContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";

const fieldCls =
  "bg-white/[0.03] border-white/15 focus-visible:border-[#D4AF37] focus-visible:ring-[#D4AF37]/30 text-white placeholder:text-white/30";

export default function LossCategoryForm({ categoryKey, guide }) {
  const { t } = useT();
  const { user } = useAuth();
  const lg = t.lossGuide || {};

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    amount_lost: "",
    call_from_agent: false,
    description: "",
    category_details: {},
  });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      name: f.name || user.name || "",
      email: f.email || user.email || "",
    }));
  }, [user]);

  const setDetail = (key, value) =>
    setForm((f) => ({
      ...f,
      category_details: { ...f.category_details, [key]: value },
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) {
      toast.error(lg.nameRequired || "Name is required");
      return;
    }
    if (!form.email?.trim()) {
      toast.error(lg.emailRequired || "Email is required");
      return;
    }
    if (!form.phone?.trim()) {
      toast.error(lg.phoneRequired || "Phone number is required");
      return;
    }
    setLoading(true);
    try {
      await api.post("/legal-requests", {
        name: form.name?.trim() || null,
        email: form.email.trim(),
        phone: form.phone?.trim() || null,
        loss_type: guide.title,
        loss_category: categoryKey,
        amount_lost: parseFloat(form.amount_lost || "0") || 0,
        call_from_agent: !!form.call_from_agent,
        description: form.description?.trim() || null,
        category_details: form.category_details,
      });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || t.common.error);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="surface-card p-10 text-center" data-testid="loss-help-success">
        <CheckCircle2 className="mx-auto h-10 w-10 text-[#10B981]" />
        <div className="mt-4 text-lg text-white/90">
          {lg.success || "Request received. Our team will review it shortly."}
        </div>
        {user && (
          <Link to="/dashboard" className="inline-block mt-6 text-sm text-[#D4AF37] hover:underline">
            {lg.viewDashboard || "View my requests on dashboard →"}
          </Link>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="surface-card p-6 md:p-8 space-y-5" data-testid="loss-help-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{lg.name || "Your name"} *</Label>
          <Input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={`mt-2 ${fieldCls}`} />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{lg.email || "Email"} *</Label>
          <Input type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={`mt-2 ${fieldCls}`} />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{lg.phone || "Phone"} *</Label>
          <Input required value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className={`mt-2 ${fieldCls}`} />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{lg.amountLost || "Amount lost (INR)"}</Label>
          <Input type="number" min="0" value={form.amount_lost} onChange={(e) => setForm((f) => ({ ...f, amount_lost: e.target.value }))} className={`mt-2 ${fieldCls}`} />
        </div>
      </div>

      <div className="border-t border-white/10 pt-5">
        <h3 className="font-display text-sm font-bold text-[#D4AF37] mb-4">
          {lg.categoryDetails || "Details for this type of loss"}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(guide.formFields || []).map((f) => (
            <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">
                {f.label}
              </Label>
              {f.type === "select" ? (
                <Select value={form.category_details[f.key] || ""} onValueChange={(v) => setDetail(f.key, v)}>
                  <SelectTrigger className={`mt-2 ${fieldCls}`}>
                    <SelectValue placeholder={lg.select || "Select…"} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white">
                    {(f.options || []).map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : f.type === "textarea" ? (
                <Textarea
                  rows={3}
                  value={form.category_details[f.key] || ""}
                  onChange={(e) => setDetail(f.key, e.target.value)}
                  className={`mt-2 resize-none ${fieldCls}`}
                />
              ) : (
                <Input
                  type={f.type === "number" ? "number" : "text"}
                  value={form.category_details[f.key] || ""}
                  onChange={(e) => setDetail(f.key, e.target.value)}
                  className={`mt-2 ${fieldCls}`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-4">
        <Checkbox
          id="call_from_agent"
          checked={form.call_from_agent}
          onCheckedChange={(v) => setForm((f) => ({ ...f, call_from_agent: !!v }))}
          className="mt-0.5 border-white/30 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
        />
        <Label htmlFor="call_from_agent" className="text-sm text-white/75 leading-relaxed cursor-pointer">
          {lg.callFromAgent ||
            "Someone claiming to be a lawyer, police officer, or recovery agent contacted me about this case"}
        </Label>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{lg.description || "What happened"}</Label>
        <Textarea
          rows={4}
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          className={`mt-2 resize-none ${fieldCls}`}
          placeholder={lg.descriptionPlaceholder || "Dates, amounts, reference numbers, and what you need help with…"}
        />
      </div>

      <Button type="submit" disabled={loading} className="w-full h-12 bg-[#D4AF37] text-black hover:bg-yellow-500 font-bold gap-2">
        <Mail className="h-4 w-4" />
        {loading ? t.common.loading : (lg.submit || "Submit to CHINTAMAT team")}
      </Button>
    </form>
  );
}
